import { useEffect, useState } from "react";
import "./style/privacyPolicy.scss";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "react-bootstrap";
import { touchlessService } from "../../services/api/touchlessService";
import Spinner from "../../components/ui/Spinner/LoadingSpinner";
import { useAppConfig } from "../../hooks/useAppConfig";
import { useVisitorData } from "../../hooks/useVisitorData";
import { BackButton } from "../../components/ui/Button/backButton";
import { useStrings } from "../../hooks/useStrings";
import { renderDocTemplate } from "../../utils/sanitizeDocTemplate";

const PrivacyPolicy = () => {
  const S = useStrings();
  const navigate = useNavigate();
  const location = useLocation();

  // Continuation routes are data-driven so the same screen can serve multiple
  // flows. Contactless Check-in passes no state and falls back to its original
  // behavior (forward to /full-name, back to /); Pre-arrival Registration
  // passes its own next/back routes via navigation state.
  const { nextRoute = "/full-name", backRoute = "/" } =
    (location.state as { nextRoute?: string; backRoute?: string } | null) ?? {};

  const { config } = useAppConfig();
  const { visitorinfo: visitorInfo } = useVisitorData();

  const [loading, setLoading] = useState(false);
  const [pdfVisitorDocument, setPdfVisitorDocument] = useState("");
  const [isAgree, setIsAgree] = useState(false);
  const [consentType, setConsentType] = useState<string>("");

  useEffect(() => {
    fetchDocument();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDocument = async () => {
    if (!config) return;

    const orgConfig = config;
    const privacyDocTemplateId = orgConfig?.privacyDocTemplateId;
    const consentType = orgConfig.privacyConsentType;

    setConsentType(consentType);

    const visitorTodayDate = touchlessService.formatDate("YYYY-MM-DD");

    try {
      setLoading(true);

      const docMeta =
        await touchlessService.getNDADocument(privacyDocTemplateId);

      const res = await fetch(docMeta.docTemplateUri);
      const rawHtml = await res.text();

      const sanitized = renderDocTemplate(rawHtml, {
        "{{visitor_name}}": (visitorInfo?.fullName as string) || "",
        "{{company}}": (visitorInfo?.company as string) || "",
        "{{visitor_phone}}": (visitorInfo?.phoneNumber as string) || "",
        "{{visitor_email}}": (visitorInfo?.email as string) || "",
        "{{org_name}}": orgConfig.orgName,
        "{{checkin_date}}": visitorTodayDate,
        "{{host_name}}": "",
        "{{visitor_type}}": "",
        "{{pointOfEntry}}": "",
        "{{parkingLot}}": "",
        "{{building}}": "",
        "{{location_name}}": "",
      });

      setPdfVisitorDocument(sanitized);
    } catch (e) {
      console.error("Failed to load privacy policy", e);
    } finally {
      setLoading(false);
    }
  };

  const onDocumentSubmit = () => {
    // Record that consent has been captured for this session so the entry
    // flows don't re-prompt (and loop) if the visitor navigates back into them.
    sessionStorage.setItem("privacyConsentGiven", "true");
    navigate(nextRoute);
  };

  const onBack = () => {
    navigate(backRoute);
  };

  return (
    <div className="main-screen" data-testid="PP-div-main-screen">
      <div
        className="screen-outer screen-signdocument"
        id="privacy-policy"
        data-testid="PP-div-screen-outer"
      >
        {/* Header */}
        <div
          className="screen-header-fixed"
          data-testid="PP-div-screen-header-fixed"
        >
          <div
            className="header-fixed-left"
            data-testid="PP-div-header-fixed-left"
          >
            <BackButton onBack={onBack} />
          </div>
          <div className="header-fixed-right" />
        </div>

        {/* Body (SCROLLS) */}
        <div className="screen-inner" data-testid="PP-div-screen-inner">
          <div className="screen-subinner">
            <div className="screen-body" data-testid="PP-div-screen-body">
              <h5 className="screen-title" data-testid="PP-title">
                {S.PRIVACY_POLICY.TITLE}
              </h5>
              <p className="screen-subtitle" data-testid="PP-subtitle">
                {S.PRIVACY_POLICY.SUBTITLE}
              </p>

              {loading && <Spinner />}

              <div
                className="pdf-visitor"
                dangerouslySetInnerHTML={{ __html: pdfVisitorDocument }}
                data-testid="PP-div-policy-visitor"
              />
            </div>

            {/* Footer (in-flow, pinned to bottom) */}
            <div className="screen-footer" data-testid="PP-div-agree-box">
              {consentType === "Explicit" && (
                <label
                  className="privacy-consent-label"
                  data-testid="PP-label-consent"
                >
                  <input
                    type="checkbox"
                    checked={isAgree}
                    onChange={() => setIsAgree(!isAgree)}
                    data-testid="PP-checkbox-agree"
                  />
                  <span>{S.PRIVACY_POLICY.EXPLICIT_CONSENT.replace('{orgName}', config?.orgName ?? '')}</span>
                </label>
              )}
              {consentType === "Implicit" && (
                <p
                  className="privacy-consent-text"
                  data-testid="PP-text-consent"
                >
                  {S.PRIVACY_POLICY.IMPLICIT_CONSENT.replace('{orgName}', config?.orgName ?? '')}
                </p>
              )}
              <Button
                type="button"
                className="btn btn-primary btn-shadow btn-width-full"
                disabled={consentType === "Explicit" && !isAgree}
                onClick={onDocumentSubmit}
                data-testid="PP-button-continue"
              >
                {S.COMMON.AGREE_AND_CONTINUE} &nbsp;&nbsp;
                <i className="bi bi-arrow-right-short"></i>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
